import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, User, LogOut, LayoutDashboard, ChefHat, Package, Calendar } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import toast from "react-hot-toast";

export default function Navbar() {
  const { user, profile, profileLoading, signOut, isAdmin, isRestaurant } = useAuth();
  const { totalItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out successfully");
    navigate("/");
    setUserMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  // Display name: only show when profile is fully loaded
  const displayName = profileLoading ? "..." : (profile?.name || user?.email?.split("@")[0] || "Account");

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl">
            <span className="text-2xl">🍽️</span>
            <span className="gradient-text">Khana Spot</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/restaurants"
              className={`text-sm font-medium transition-colors ${
                isActive("/restaurants") ? "text-orange-500" : "text-gray-600 hover:text-orange-500"
              }`}
            >
              Restaurants
            </Link>
            {user && !isAdmin && !isRestaurant && (
              <Link
                to="/my-orders"
                className={`text-sm font-medium transition-colors ${
                  isActive("/my-orders") ? "text-orange-500" : "text-gray-600 hover:text-orange-500"
                }`}
              >
                My Orders
              </Link>
            )}
            {user && !isAdmin && !isRestaurant && (
              <Link
                to="/my-bookings"
                className={`text-sm font-medium transition-colors ${
                  isActive("/my-bookings") ? "text-orange-500" : "text-gray-600 hover:text-orange-500"
                }`}
              >
                My Bookings
              </Link>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Cart — only for regular users */}
            {!isAdmin && !isRestaurant && (
              <Link
                to="/checkout"
                className="relative p-2 text-gray-600 hover:text-orange-500 transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-scale-in">
                    {totalItems}
                  </span>
                )}
              </Link>
            )}

            {/* User menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-2 rounded-xl hover:bg-orange-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    {profileLoading ? (
                      <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <User className="w-4 h-4 text-orange-600" />
                    )}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
                    {displayName}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 animate-fade-in">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {profileLoading ? "Loading..." : profile?.name}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {profileLoading ? "..." : profile?.role}
                      </p>
                    </div>

                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                      >
                        <LayoutDashboard className="w-4 h-4" /> Admin Panel
                      </Link>
                    )}
                    {isRestaurant && (
                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                      >
                        <ChefHat className="w-4 h-4" /> Restaurant Panel
                      </Link>
                    )}
                    {!isAdmin && !isRestaurant && (
                      <>
                        <Link
                          to="/my-orders"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                        >
                          <Package className="w-4 h-4" /> My Orders
                        </Link>
                        <Link
                          to="/my-bookings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                        >
                          <Calendar className="w-4 h-4" /> My Bookings
                        </Link>
                        <Link
                          to="/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                        >
                          <User className="w-4 h-4" /> Profile
                        </Link>
                      </>
                    )}
                    <hr className="my-1 border-gray-100" />
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" className="btn-ghost text-sm">Sign In</Link>
                <Link to="/register" className="btn-primary text-sm py-2 px-4">Sign Up</Link>
              </div>
            )}

            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-3 animate-fade-in">
          <Link to="/restaurants" className="block py-2 text-gray-700 font-medium" onClick={() => setMenuOpen(false)}>
            🍽️ Restaurants
          </Link>
          {!user ? (
            <>
              <Link to="/login" className="block py-2 text-gray-700 font-medium" onClick={() => setMenuOpen(false)}>Sign In</Link>
              <Link to="/register" className="block btn-primary text-center" onClick={() => setMenuOpen(false)}>Sign Up</Link>
            </>
          ) : (
            <>
              {isAdmin && <Link to="/admin" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>⚙️ Admin Panel</Link>}
              {isRestaurant && <Link to="/dashboard" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>🍽️ Dashboard</Link>}
              {!isAdmin && !isRestaurant && (
                <Link to="/my-orders" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>📦 My Orders</Link>
              )}
              {!isAdmin && !isRestaurant && (
                <Link to="/my-bookings" className="block py-2 text-gray-700" onClick={() => setMenuOpen(false)}>🪑 My Bookings</Link>
              )}
              <button onClick={handleSignOut} className="block w-full text-left py-2 text-red-600 font-medium">Sign Out</button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
